import { SelectProps } from '@amzn/awsui-components-core/select';
import { useCallback, useEffect, useState } from 'react';
import { isVisibleVersion } from 'src/components/star-grid/utils/versionAvailability';
import { PnlDataVersion, VersionChoices } from 'src/components/star-grid/reportSelectors.types';
import { isResourcePending, useAsyncResource } from 'src/components/star-grid/hooks/useAsyncResource';
import { useDataSyncText } from 'src/components/star-grid/hooks/useDataSyncText';
import { listPnlDecks, listPnlVersions } from 'src/components/star-grid/services/starGridService';
import { PnlReportConfig } from 'src/components/star-grid/starGridTypes';
import { deckOptions, selectedVersionOption, versionOptions } from 'src/components/star-grid/utils/reportSelectorOptions';

export interface ReportSelectorControls {
  readonly version: SelectProps;
  readonly versionChoices: VersionChoices;
  readonly deck: SelectProps;
}
interface SelectorProps {
  readonly definition?: PnlReportConfig;
  readonly asOfTimestamp?: string;
  readonly onVersionChange: (timestamp?: string) => void;
  readonly onOpenReport?: (reportId: number) => void;
}
interface CatalogMessages {
  readonly placeholder: string;
  readonly loadingText: string;
  readonly errorText: string;
  readonly empty: string;
}

const useSelectorCatalog = <T>(fetcher: () => Promise<T>, messages: CatalogMessages) => {
  const text = useDataSyncText();
  const resource = useAsyncResource(fetcher);
  useEffect(() => resource.ensureLoaded(), [resource.ensureLoaded]);
  const control: Partial<SelectProps> = {
    ...messages,
    filteringType: 'auto',
    filteringPlaceholder: text('findValue'),
    expandToViewport: true,
    statusType: isResourcePending(resource) ? 'loading' : resource.error ? 'error' : 'finished',
    recoveryText: text('retryCatalog'),
    noMatch: text('noMatchingValues'),
    onLoadItems: ({ detail }) => {
      if (resource.error && detail.samePage) resource.refresh();
    }
  };
  return { data: resource.data, control, retry: resource.refresh };
};

const useVersionCatalog = (messages: CatalogMessages) => {
  const [historyIncomplete, setHistoryIncomplete] = useState(false);
  const fetchVersions = useCallback(async () => {
    setHistoryIncomplete(false);
    return listPnlVersions(() => setHistoryIncomplete(true));
  }, []);
  const catalog = useSelectorCatalog(fetchVersions, messages);
  return { ...catalog, historyIncomplete };
};

const useVersionSelector = (props: SelectorProps): { control: SelectProps; choices: VersionChoices } => {
  const text = useDataSyncText();
  const catalog = useVersionCatalog({
    placeholder: text('chooseVersion'),
    loadingText: text('loadingVersions'),
    errorText: text('versionsUnavailable'),
    empty: text('noVersions')
  });
  const versions = (catalog.data ?? []).filter(isVisibleVersion);
  const select = (version: PnlDataVersion): void => {
    if (props.definition && isVisibleVersion(version) && version.resolvable !== false) props.onVersionChange(version.as_of ?? undefined);
  };
  const control: SelectProps = {
    ...catalog.control,
    options: versionOptions(versions, text('versionHistoryUnavailable')),
    selectedOption: selectedVersionOption(versions, props.asOfTimestamp),
    disabled: !props.definition,
    onChange: ({ detail }) => {
      const selected = versions.find((item) => item.version === detail.selectedOption.value);
      if (selected) select(selected);
    }
  };
  return { control, choices: { versions, select, retry: catalog.retry, historyIncomplete: catalog.historyIncomplete } };
};

const useDeckSelector = (props: SelectorProps): SelectProps => {
  const text = useDataSyncText();
  const catalog = useSelectorCatalog(listPnlDecks, {
    placeholder: text('chooseDeck'),
    loadingText: text('loadingDecks'),
    errorText: text('decksUnavailable'),
    empty: text('noDecks')
  });
  const options = deckOptions(catalog.data ?? [], props.definition, text('unassignedDeck'));
  const current = props.definition;
  const fallback = current
    ? { value: String(current.pnl_report_id), label: current.deck_type ?? text('unassignedDeck'), description: current.report_name }
    : null;
  return {
    ...catalog.control,
    options,
    selectedOption: options.find((option) => option.value === String(current?.pnl_report_id)) ?? fallback,
    disabled: !current || !props.onOpenReport,
    onChange: ({ detail }) => {
      const selected = options.find((option) => option.value === detail.selectedOption.value);
      if (selected?.value) props.onOpenReport?.(Number(selected.value));
    }
  };
};

export const useReportSelectors = (props: SelectorProps): ReportSelectorControls => {
  const version = useVersionSelector(props);
  return { version: version.control, versionChoices: version.choices, deck: useDeckSelector(props) };
};
